import {
  useBodyComposition,
  useBodyMap,
  useDashboardWindows,
  useHomeBriefing,
  useLatestAnalysis,
  useProfile,
  useReference,
  useStanding,
  useSummary,
  useTimeline,
  useVolume,
} from '../../api/hooks'
import { panelOf, type PanelState, type QueryReachability } from '../../lib/queryState'

/**
 * What each dashboard panel is going to render (Spec 2 D5, #170).
 *
 * Every panel reads its data and its state from one of these hooks, and the
 * Dashboard reads the same hooks to decide whether a section, or the whole page,
 * collapses to one card (the A+ ruling recorded on #169: one failed panel shows
 * its own card, a section whose panels all failed shows one, a page whose
 * sections all failed shows one). One definition per panel, so the panel and the
 * collapse cannot disagree about what the panel can draw; TanStack dedupes the
 * repeated query calls. The Journey does the same, per section, in its
 * `panelStates.ts` files, and all three surfaces collapse by the one rule,
 * `failsAsOne` in `lib/queryState.ts`.
 *
 * `hasContent` is what the panel can DRAW. Most panels can draw any loaded
 * response, zeros included, because zero is real data there; where "nothing"
 * already had its own treatment (no scans hides the body-composition section,
 * no session leaves the digest blank), only real content counts.
 *
 * **Every dashboard panel draws nothing when it is empty**: no scans hides
 * section 03, no session leaves the digest blank, no analysis hides 06 to 08,
 * and the rest supply no empty state at all. So each one says so to the shared
 * collapse, `failsAsOne`, which then neither lets a cached "no scans" answer
 * hold a section open through an outage nor collapses anything over it alone
 * (#215). A panel added here with an empty state of its own must not go through
 * `panel()` unchanged.
 */
export interface DashboardPanel<T> {
  data: T | undefined
  sources: Partial<QueryReachability>[]
  hasContent: boolean
  state: PanelState
  emptyDrawsNothing: boolean
}

function panel<T>(
  data: T | undefined,
  sources: Partial<QueryReachability>[],
  hasContent: boolean,
): DashboardPanel<T> {
  return { data, ...panelOf(sources, hasContent, { emptyDrawsNothing: true }) }
}

/** The weekly tonnage chart (05): `GET /api/volume`'s weekly series. */
export function useVolumePanel() {
  const q = useVolume()
  return panel(q.data, [q], q.data !== undefined)
}

/**
 * The rolling-range counters (02). Sessions, tonnage and working sets are
 * `GET /api/volume`'s — the one calculation, the same figure the Sessions page
 * shows for a day inside the range — and the sleep average is the windows
 * endpoint's. Both have to load for the four counters to draw together.
 */
export function useRangeCountersPanel() {
  const volume = useVolume()
  const windows = useDashboardWindows()
  const data =
    volume.data !== undefined && windows.data !== undefined
      ? { volume: volume.data, windows: windows.data }
      : undefined
  return panel(data, [volume, windows], data !== undefined)
}

/** Body composition (03). No scans at all hides the section, as it always has. */
export function useScanPanel() {
  const q = useBodyComposition()
  return panel(q.data, [q], !!q.data?.length)
}

/** The strength reference table (04). */
export function useReferencePanel() {
  const q = useReference()
  return panel(q.data, [q], q.data !== undefined)
}

/** The body map beside it (04). */
export function useBodyMapPanel() {
  const q = useBodyMap()
  return panel(q.data, [q], q.data !== undefined)
}

/** Weekly attendance (05). */
export function useAttendancePanel() {
  const q = useTimeline()
  return panel(q.data, [q], q.data !== undefined)
}

/** The last session's digest (05). A user with no session yet has nothing to digest. */
export function useDigestPanel() {
  const q = useHomeBriefing()
  return panel(q.data, [q], !!q.data?.last_session)
}

/** The hero (01), which only draws once all four of its sources are in, so its
 *  data exists only then. */
export function useHeroPanel() {
  const profile = useProfile()
  const summary = useSummary()
  const standing = useStanding()
  const briefing = useHomeBriefing()
  const complete =
    profile.data && summary.data && standing.data && briefing.data
      ? { profile: profile.data, summary: summary.data, standing: standing.data, briefing: briefing.data }
      : undefined
  return panel(complete, [profile, summary, standing, briefing], complete !== undefined)
}

/** Sections 06 to 08, which share one analysis. `null` means none generated yet. */
export function useAnalysisPanel(enabled: boolean = true) {
  const q = useLatestAnalysis(enabled)
  return panel(q.data, [q], q.data != null)
}
